// src/components/ThreatActorsView.js
// Threat Actor Knowledge Base — cards grid index + dossier detail.
// Self-contained: grid <-> dossier swap via internal state (no router changes).
// Reads from /api/threat-actors (list) and /api/threat-actors/by-slug/:slug (dossier).
// Mirrors RansomwareView conventions: apiFetch 401-retry, slate theme, lucide icons.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Users, Search, Plus, Shield, AlertTriangle, Globe, Target, Wrench,
  Activity, Bug, ArrowLeft, RefreshCw, Database, Tag, FileText,
  ChevronRight, MapPin, Flag, Crosshair, Layers, ExternalLink
} from 'lucide-react';
import AddThreatActorModal from './AddThreatActorModal';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

// Display-casing: DB stores ransomware names lowercase (dragonforce, safepay).
// Known brand-casings get fixed here; everything else gets a sensible Title Case.
// Data stays clean lowercase in the DB — this is presentation only.
const BRAND_CASING = {
  lockbit: 'LockBit', blackcat: 'BlackCat', blackbasta: 'Black Basta',
  blackbyte: 'BlackByte', blacksuit: 'BlackSuit', ransomhub: 'RansomHub',
  dragonforce: 'DragonForce', safepay: 'SafePay', bianlian: 'BianLian',
  avoslocker: 'AvosLocker', noescape: 'NoEscape', helldown: 'Helldown',
  ragnarlocker: 'RagnarLocker', medusalocker: 'MedusaLocker', hellokitty: 'HelloKitty',
  ransomhouse: 'RansomHouse', vicesociety: 'ViceSociety', worldleaks: 'WorldLeaks',
  cicada3301: 'Cicada3301', '8base': '8Base', threeam: 'ThreeAM',
  killsec: 'KillSec', nightspire: 'NightSpire', spacebears: 'SpaceBears',
  payoutsking: 'PayoutsKing', arcusmedia: 'ArcusMedia', coinbasecartel: 'CoinbaseCartel',
  raworld: 'RAWorld', thegentlemen: 'The Gentlemen', funksec: 'FunkSec',
  apt73: 'APT73', incransom: 'INC Ransom', shinyhunters: 'ShinyHunters',
};

const displayName = (name) => {
  if (!name) return '';
  const key = name.toLowerCase().replace(/[\s\-_.]/g, '');
  if (BRAND_CASING[key]) return BRAND_CASING[key];
  // Already mixed-case (APT28, Volt Typhoon) — leave as-is
  if (/[A-Z]/.test(name)) return name;
  // All-lowercase fallback: title-case each word
  return name.replace(/\b\w/g, (c) => c.toUpperCase());
};

const fmt = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(d); }
};

const initials = (name = '') =>
  name.split(/[\s\-_]+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');

// apiFetch — mirrors RansomwareView: takes getToken fn, retries once on 401 with fresh token.
const apiFetch = async (path, getToken, options = {}) => {
  const doFetch = async (tok) => fetch(`/api/threat-actors${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${tok}`, 'Accept': 'application/json', ...options.headers }
  });
  let tok = await getToken();
  let res = await doFetch(tok);
  if (res.status === 401) {
    tok = await getToken({ skipCache: true });
    res = await doFetch(tok);
  }
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `HTTP ${res.status}`);
  }
  return res.json();
};

const motivationStyle = (m) => {
  const v = (m || '').toLowerCase();
  if (v.includes('state')) return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
  if (v.includes('financ')) return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
  if (v.includes('hack')) return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
  if (v.includes('espionage')) return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
  return 'bg-slate-600/30 text-slate-300 border-slate-600/40';
};

const sourceBadge = (s) => {
  if (s === 'unit42') return 'bg-blue-500/20 text-blue-300';
  if (s === 'ransomware.live') return 'bg-red-500/20 text-red-300';
  if (s === 'telegram') return 'bg-sky-500/20 text-sky-300';
  if (s === 'mitre') return 'bg-purple-500/20 text-purple-300';
  return 'bg-slate-600/30 text-slate-300';
};

// IOC type → pill color. Tolerant of casing / partial type strings.
const getIocTypeColor = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('ip')) return 'bg-blue-500/20 text-blue-300';
  if (t.includes('domain')) return 'bg-purple-500/20 text-purple-300';
  if (t.includes('hash') || t.includes('sha') || t.includes('md5')) return 'bg-emerald-500/20 text-emerald-300';
  if (t.includes('email')) return 'bg-amber-500/20 text-amber-300';
  if (t.includes('url')) return 'bg-cyan-500/20 text-cyan-300';
  return 'bg-slate-600/30 text-slate-300';
};

// The list endpoint doesn't return a sources array, but created_by encodes origin
// ('unit42-seed', 'ransomware.live-import'). Derive a badge from it.
const cardSources = (actor) => {
  if (Array.isArray(actor.sources) && actor.sources.length) return actor.sources;
  const cb = (actor.created_by || '').toLowerCase();
  if (cb.includes('unit42')) return ['unit42'];
  if (cb.includes('ransomware')) return ['ransomware.live'];
  if (cb.includes('mitre')) return ['mitre'];
  return [];
};

// ─── SHARED UI ────────────────────────────────────────────────────────────────
const Spinner = ({ label = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <RefreshCw className="w-7 h-7 text-cyan-400 animate-spin" />
    <span className="text-slate-500 text-sm">{label}</span>
  </div>
);

const Empty = ({ icon: Icon = Database, title, sub }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-8">
    <div className="w-14 h-14 rounded-2xl bg-slate-800/60 border border-slate-700/40 flex items-center justify-center">
      <Icon className="w-6 h-6 text-slate-500" />
    </div>
    <div className="text-slate-300 font-medium">{title}</div>
    {sub && <div className="text-slate-500 text-sm max-w-sm">{sub}</div>}
  </div>
);

const Section = ({ icon: Icon, title, count, children }) => (
  <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
      <Icon className="w-5 h-5 text-cyan-400" />
      {title}
      {count != null && (
        <span className="ml-1 px-2 py-0.5 bg-slate-700/50 rounded-full text-xs text-slate-400 font-normal">{count}</span>
      )}
    </h3>
    {children}
  </div>
);

// ─── CARD ─────────────────────────────────────────────────────────────────────
const ActorCard = ({ actor, onClick }) => {
  const aliasPreview = (actor.aliases || [])
    .filter(a => !a.is_primary)
    .slice(0, 3)
    .map(a => a.alias)
    .join(', ');

  return (
    <div
      onClick={onClick}
      className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 cursor-pointer hover:border-cyan-500/40 hover:bg-slate-800/60 transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-100 text-sm truncate">{displayName(actor.canonical_name || actor.name)}</h3>
          {aliasPreview && (
            <div className="text-xs text-slate-500 truncate mt-0.5">aka {aliasPreview}</div>
          )}
        </div>
        {actor.mitre_id && (
          <span className="shrink-0 font-mono text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">{actor.mitre_id}</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 my-2">
        {actor.attributed_country && (
          <span className="px-2 py-0.5 bg-slate-700/40 text-slate-300 rounded text-[11px]">{actor.attributed_country}</span>
        )}
        {actor.motivation && (
          <span className={`px-2 py-0.5 rounded text-[11px] border ${motivationStyle(actor.motivation)}`}>{actor.motivation}</span>
        )}
      </div>

      <div className="flex items-center gap-4 pt-2 border-t border-slate-700/40 text-[11px] text-slate-400">
        <span className="flex items-center gap-1"><Target className="w-3 h-3" />{(actor.sectors || []).length}</span>
        <span className="flex items-center gap-1"><Wrench className="w-3 h-3" />{(actor.tools || []).length}</span>
        <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{actor.ioc_count || 0}</span>
        <div className="ml-auto flex gap-1">
          {cardSources(actor).map(s => (
            <span key={s} className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${sourceBadge(s)}`}>{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── DOSSIER ──────────────────────────────────────────────────────────────────
const Dossier = ({ slug, getToken, onBack, canEdit, onEdit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activeProfile, setActiveProfile] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true); setErr(null);
        const d = await apiFetch(`/by-slug/${encodeURIComponent(slug)}`, getToken);
        if (alive) setData(d);
      } catch (e) {
        if (alive) setErr(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug, getToken]);

  if (loading) return <Spinner label="Loading dossier..." />;
  if (err) return <Empty icon={AlertTriangle} title="Couldn't load dossier" sub={err} />;
  if (!data) return null;

  const profiles = data.profiles || [];
  const aliases = data.aliases || [];
  const prof = profiles[activeProfile] || {};
  const tools = data.tools || [];          // merged superset from getActorWithProfiles
  const sectors = data.sectors || [];
  const countries = data.countries || [];
  const techniques = data.mitre_techniques || [];
  const iocs = data.iocs || [];
  const victims = data.victims || [];

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30 sticky top-0 z-10 backdrop-blur">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-400 mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> All threat actors
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-slate-100">{displayName(data.canonical_name || data.name)}</h2>
              {data.mitre_id && (
                <span className="font-mono text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded">{data.mitre_id}</span>
              )}
            </div>
            {aliases.length > 0 && (
              <div className="text-sm text-slate-400 mt-2">
                also known as {aliases.filter(a => !a.is_primary).map(a => a.alias).slice(0, 8).join(' · ') || '—'}
              </div>
            )}
            <div className="flex flex-wrap gap-2 items-center mt-3">
              {data.attributed_country && (
                <span className="px-3 py-1 bg-slate-700/50 rounded-lg text-sm text-slate-300 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />{data.attributed_country}
                </span>
              )}
              {data.motivation && (
                <span className={`px-3 py-1 rounded-lg text-sm border ${motivationStyle(data.motivation)}`}>{data.motivation}</span>
              )}
              {data.status && (
                <span className={`px-3 py-1 rounded-lg text-sm ${
                  data.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                }`}>{data.status}</span>
              )}
              {(data.first_seen || data.last_activity) && (
                <span className="px-3 py-1 bg-slate-700/30 rounded-lg text-sm text-slate-400">
                  {data.first_seen ? fmt(data.first_seen) : '?'} → {data.last_activity ? fmt(data.last_activity) : 'present'}
                </span>
              )}
            </div>
          </div>
          {canEdit && (
            <button onClick={() => onEdit(data)} className="shrink-0 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-sm text-blue-400 transition-all">
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-4xl space-y-6">

          {/* Overview — per-source tabs */}
          {profiles.length > 0 && (
            <Section icon={Shield} title="Overview" count={`${profiles.length} source${profiles.length !== 1 ? 's' : ''}`}>
              {profiles.length > 1 && (
                <div className="flex gap-2 mb-4 border-b border-slate-700/50">
                  {profiles.map((p, i) => (
                    <button key={p.source} onClick={() => setActiveProfile(i)}
                      className={`px-3 py-1.5 text-xs font-mono -mb-px border-b-2 transition-colors ${
                        i === activeProfile ? 'border-cyan-400 text-slate-100' : 'border-transparent text-slate-400 hover:text-slate-200'
                      }`}>
                      {p.source}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
                {prof.summary || data.summary || 'No overview available from this source.'}
              </p>
              {prof.source_url && (
                <a href={prof.source_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 mt-3">
                  <ExternalLink className="w-3 h-3" /> source
                </a>
              )}
            </Section>
          )}

          {/* Aliases */}
          {aliases.length > 0 && (
            <Section icon={Tag} title="Aliases" count={aliases.length}>
              <div className="flex flex-wrap gap-2">
                {aliases.map((a) => (
                  <span key={a.id || a.alias}
                    className={`px-2.5 py-1 rounded-md text-xs border ${
                      a.is_primary ? 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40'
                      : a.source === 'mitre' ? 'bg-purple-500/20 text-purple-200 border-purple-500/40 font-mono'
                      : 'bg-slate-800/60 text-slate-300 border-slate-700/50'
                    }`}>
                    {a.alias}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Targeting */}
          {(sectors.length > 0 || countries.length > 0) && (
            <Section icon={Crosshair} title="Targeting">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {sectors.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Sectors</div>
                    <div className="flex flex-wrap gap-2">
                      {sectors.map((s, i) => (
                        <span key={i} className="px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-md text-xs border border-purple-500/30">
                          {s.sector || s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {countries.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Countries</div>
                    <div className="flex flex-wrap gap-2">
                      {countries.map((c, i) => (
                        <span key={i} className="px-2.5 py-1 bg-blue-500/20 text-blue-300 rounded-md text-xs border border-blue-500/30">
                          {c.country || c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* TTPs */}
          {techniques.length > 0 && (
            <Section icon={Activity} title="MITRE ATT&CK techniques" count={techniques.length}>
              <div className="space-y-2">
                {techniques.map((t, i) => (
                  <div key={i} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex items-center justify-between">
                    <div>
                      <span className="font-mono text-cyan-400 text-sm">{t.technique_id}</span>
                      {t.technique_name && <span className="text-slate-200 text-sm ml-3">{t.technique_name}</span>}
                    </div>
                    {t.tactic && <span className="px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-400">{t.tactic}</span>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Tools & Malware */}
          {tools.length > 0 && (
            <Section icon={Wrench} title="Tools & malware" count={tools.length}>
              <div className="space-y-2">
                {tools.map((t, i) => (
                  <div key={i} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <div className="font-medium text-slate-200 text-sm">{t.tool_name || t.name}</div>
                    {(t.tool_description || t.description) && (
                      <div className="text-xs text-slate-400 mt-1">{t.tool_description || t.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Recent victims — from ransomware.live + Telegram via propagator */}
          {victims.length > 0 && (
            <Section icon={Crosshair} title="Recent victims" count={victims.length}>
              <div className="space-y-2">
                {victims.slice(0, 50).map((v, i) => (
                  <div key={v.id || i} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-slate-200 text-sm font-medium truncate">{v.victim_name}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                        {v.victim_country && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{v.victim_country}</span>}
                        {v.sector && <span>{v.sector}</span>}
                        {v.posted_at && <span>{fmt(v.posted_at)}</span>}
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-wrap gap-1 justify-end">
                      {(v.sources || []).map(s => (
                        <span key={s} className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${sourceBadge(s)}`}>{s}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Indicators of Compromise */}
          {iocs.length > 0 && (
            <Section icon={AlertTriangle} title="Indicators of compromise" count={iocs.length}>
              <div className="space-y-2">
                {iocs.slice(0, 100).map((ioc, i) => (
                  <div key={ioc.id || i} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex items-start gap-3">
                    <span className={`shrink-0 px-2 py-1 rounded text-xs font-medium ${getIocTypeColor(ioc.ioc_type)}`}>{ioc.ioc_type}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-slate-200 text-sm break-all">{ioc.ioc_value}</div>
                      {ioc.description && <div className="text-xs text-slate-400 mt-1">{ioc.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Only show the "coming soon" note when every feed-driven section is empty */}
          {victims.length === 0 && iocs.length === 0 && techniques.length === 0 && (
            <div className="text-xs text-slate-600 text-center py-4 border-t border-slate-800/40">
              Recent victims, IOCs, and detection rules will appear here as the ransomware.live and Telegram feeds sync.
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const ThreatActorsView = ({ isSuperAdmin, canEditIncidents }) => {
  const { getToken, isSignedIn } = useAuth();
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [motivationFilter, setMotivationFilter] = useState('');
  const [activeSlug, setActiveSlug] = useState(null);   // null = grid, else = dossier
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingActor, setEditingActor] = useState(null);

  const loadActors = useCallback(async () => {
    try {
      setLoading(true); setErr(null);
      const data = await apiFetch('', getToken);
      // endpoint returns { actors, count }
      setActors(Array.isArray(data) ? data : (data.actors || []));
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (isSignedIn) loadActors();
  }, [isSignedIn, loadActors]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return actors.filter(a => {
      if (motivationFilter && (a.motivation || '').toLowerCase() !== motivationFilter) return false;
      if (!q) return true;
      const hay = [
        a.canonical_name, a.name, a.attributed_country,
        ...(a.aliases || []).map(x => x.alias)
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [actors, search, motivationFilter]);

  const handleAdd = async (formData) => {
    try {
      await apiFetch('', getToken, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      setIsAddModalOpen(false);
      await loadActors();
    } catch (e) { alert('Failed to create threat actor: ' + e.message); }
  };

  // ── Dossier view ──
  if (activeSlug) {
    return (
      <div className="h-full flex flex-col">
        <Dossier
          slug={activeSlug}
          getToken={getToken}
          canEdit={canEditIncidents}
          onEdit={(a) => setEditingActor(a)}
          onBack={() => setActiveSlug(null)}
        />
        <AddThreatActorModal
          isOpen={editingActor !== null}
          onClose={() => setEditingActor(null)}
          onSubmit={() => { setEditingActor(null); }}
          editData={editingActor}
        />
      </div>
    );
  }

  // ── Grid view ──
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            Threat Actors
          </h2>
          {isSuperAdmin && (
            <button onClick={() => setIsAddModalOpen(true)}
              className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs text-white font-medium transition-all flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add Actor
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search names, aliases, countries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>
          <select value={motivationFilter} onChange={(e) => setMotivationFilter(e.target.value)}
            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50">
            <option value="">All motivations</option>
            <option value="state-sponsored">State-sponsored</option>
            <option value="financial">Financial</option>
            <option value="espionage">Espionage</option>
          </select>
        </div>

        <div className="mt-3 text-xs text-slate-400">
          {filtered.length} of {actors.length} threat actors
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <Spinner label="Loading threat actors..." />
        ) : err ? (
          <Empty icon={AlertTriangle} title="Couldn't load threat actors" sub={err} />
        ) : filtered.length === 0 ? (
          <Empty icon={Users} title={search || motivationFilter ? 'No matches' : 'No threat actors yet'}
            sub={search || motivationFilter ? 'Try a different search or filter.' : null} />
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {filtered.map((actor) => (
              <ActorCard key={actor.id} actor={actor} onClick={() => setActiveSlug(actor.slug || actor.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Add Modal */}
      <AddThreatActorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAdd}
        editData={null}
      />
    </div>
  );
};

export default ThreatActorsView;